"use client";

import { useEffect, useRef, useState } from "react";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import {
  fetchServiceMonitoring,
  fetchServiceRegulamin,
  fetchUserServices,
  type ServiceMonitoring,
  type ServiceRegulamin,
  type UserService,
} from "@/lib/api/c-crm-project-management-user";
import styles from "./CCrmProjectManagement.module.css";

/**
 * Panel pojedynczej usługi (plugin c_crm_project_management, zakładka `services`).
 *
 * Drill-down z listy usług, z własnym adresem (`#extension-services,pm-s{id}` -
 * zob. [taskRoute.ts](./taskRoute.ts)), więc panel da się wysłać klientowi linkiem.
 *
 * **Zakładki:** informacje o usłudze oraz regulamin, na jaki klient się zgodził.
 * Regulamin siedzi w osobnej zakładce, a nie pod opisem usługi - to długi dokument,
 * który przy jednym widoku spychałby dane usługi poza ekran.
 *
 * **Regulamin wczytujemy dopiero po wejściu w zakładkę** (osobny endpoint): to duży
 * HTML, a większość wejść w panel dotyczy terminu i przedłużenia, nie warunków.
 * Wynik zostaje w stanie, więc przełączanie zakładek nie strzela do API ponownie.
 *
 * **Dane bierzemy z listy usług** (`userServices`), a nie z osobnego endpointu:
 * ten sam wiersz niesie już komplet informacji wraz z policzonym po stronie serwera
 * adresem przedłużenia i flagami dat. Osobny endpoint musiałby powtórzyć całą tę
 * logikę, a zakres dostępu i tak liczy backend - usługa spoza projektów klienta
 * po prostu nie znajdzie się w odpowiedzi i panel pokaże komunikat o braku dostępu.
 *
 * **Przedłużenie ma cztery różne stany i każdy mówi co innego:**
 *   - `auto_renew` -> usługa przedłuża się sama, klient nie ma tu nic do zrobienia,
 *   - `renew_href` -> przedłużenie możliwe TERAZ (przycisk do panelu przedłużenia),
 *   - `renew_allowed === false` -> pakiet w ogóle nie przewiduje przedłużania,
 *   - pakiet przedłużalny, ale bez adresu -> okno przedłużenia jeszcze się nie otworzyło
 *     albo już minęło (klient ma wrócić bliżej terminu / skontaktować się z nami).
 * Bez tego rozróżnienia brak przycisku byłby dla klienta nieodróżnialny od awarii.
 */

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). */
function formatDate(raw: string | null): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

/** Polska odmiana "dzień/dni" - 1 dzień, 2-4 dni, 5+ dni. */
function dayWord(n: number): string {
  return n === 1 ? "dzień" : "dni";
}

/** Zakładki panelu usługi. */
type ServiceTab = "info" | "hosting" | "monitoring" | "regulamin";

export function ServiceDetail({
  serviceId,
  onBack,
}: {
  serviceId: number;
  onBack: () => void;
}) {
  const [service, setService] = useState<UserService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ServiceTab>("info");
  const [monitoring, setMonitoring] = useState<ServiceMonitoring | null>(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState<string | null>(null);
  //ID usługi, dla której zapytanie o monitoring już poszło (ten sam wzorzec co przy regulaminie)
  const monitoringRequestedFor = useRef<number | null>(null);
  const [regulamin, setRegulamin] = useState<ServiceRegulamin | null>(null);
  const [regulaminLoading, setRegulaminLoading] = useState(false);
  const [regulaminError, setRegulaminError] = useState<string | null>(null);
  //ID usługi, dla której zapytanie o regulamin już poszło (zob. efekt niżej)
  const requestedFor = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        /* Adres powrotu z panelu przedłużenia - jak na liście usług, zna go tylko
           przeglądarka (backend obsługuje wiele domen CMS). Tu wracamy wprost do
           panelu tej usługi, a nie na listę. */
        const returnUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${window.location.pathname}${window.location.hash}`
            : undefined;
        const { services } = await fetchUserServices(returnUrl);
        if (cancelled) return;
        setService(services.find((s) => s.id === serviceId) ?? null);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  /* Regulamin dociągamy przy PIERWSZYM wejściu w zakładkę i trzymamy w stanie -
     kolejne przełączenia nie wołają API.
     Fakt wysłania zapytania trzymamy w REF, nie w stanie: stan trafiłby do listy
     zależności, a jego zmiana odpaliłaby sprzątanie poprzedniego przebiegu efektu -
     czyli anulowała odpowiedź, na którą właśnie czekamy.
     Ref jest też tym, co pilnuje ZMIANY usługi: dokument poprzedniej znika ze stanu
     razem z wysłaniem nowego zapytania, więc panel nigdy nie pokaże cudzych warunków. */
  /* Historię zdarzeń dociągamy przy PIERWSZYM wejściu w zakładkę - stan strony jest już
     w liście usług, a zdarzeń bywa kilkadziesiąt na usługę. Ref zamiast stanu z tego samego
     powodu co przy regulaminie: stan w zależnościach anulowałby własne zapytanie. */
  useEffect(() => {
    if (tab !== "monitoring" || monitoringRequestedFor.current === serviceId) return;
    monitoringRequestedFor.current = serviceId;

    let cancelled = false;
    setMonitoring(null);
    setMonitoringLoading(true);
    setMonitoringError(null);
    (async () => {
      try {
        const data = await fetchServiceMonitoring(serviceId);
        if (cancelled) return;
        setMonitoring(data);
        setMonitoringLoading(false);
      } catch (e) {
        if (cancelled) return;
        setMonitoringError(e instanceof Error ? e.message : String(e));
        setMonitoringLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, serviceId]);

  useEffect(() => {
    if (tab !== "regulamin" || requestedFor.current === serviceId) return;
    requestedFor.current = serviceId;

    let cancelled = false;
    setRegulamin(null);
    setRegulaminLoading(true);
    setRegulaminError(null);
    (async () => {
      try {
        const data = await fetchServiceRegulamin(serviceId);
        if (cancelled) return;
        setRegulamin(data);
      } catch (e) {
        if (cancelled) return;
        setRegulaminError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setRegulaminLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, serviceId]);

  const header = (
    <div className={styles.panelHeader}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        <Icon name="caret-left" />
        Usługi
      </button>
      <h3 className={styles.panelTitle}>
        Panel usługi
        {service && <span className={styles.panelNr}>({service.nr})</span>}
      </h3>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.root}>
        {header}
        <FormStatus variant="info">Wczytywanie usługi...</FormStatus>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        {header}
        <FormStatus variant="error">{error}</FormStatus>
      </div>
    );
  }

  if (!service) {
    return (
      <div className={styles.root}>
        {header}
        <FormStatus variant="warning">
          Nie znaleźliśmy tej usługi wśród usług z Twoich projektów. Sprawdź adres
          albo wróć do listy usług.
        </FormStatus>
      </div>
    );
  }

  const daysAfter = service.expiry_days !== null && service.expiry_days < 0 ? -service.expiry_days : 0;
  const daysLeft = service.expiry_days !== null && service.expiry_days >= 0 ? service.expiry_days : null;
  //Kolor tylko wzmacnia etykiete - status jest zawsze wypisany tekstem (WCAG 1.4.1).
  const statusVariant = !service.is_current ? "neutral" : service.is_expired ? "warning" : "success";

  return (
    <div className={styles.root}>
      {header}

      <div className={styles.tabs} role="tablist" aria-label="Sekcje panelu usługi">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "info"}
          className={`${styles.tab} ${tab === "info" ? styles.tabActive : ""}`}
          onClick={() => setTab("info")}
        >
          <Icon name="info" />
          Informacje
        </button>
        {/* Zakładka hostingu tylko dla usług, którym processing założył konto na serwerze -
            dla pozostałych `hosting` jest nullem i nie ma dokąd wysyłać klienta. */}
        {service.hosting && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "hosting"}
            className={`${styles.tab} ${tab === "hosting" ? styles.tabActive : ""}`}
            onClick={() => setTab("hosting")}
          >
            <Icon name="gear" />
            Hosting
          </button>
        )}
        {/* Zakładka monitoringu tylko dla usług, które przebieg zdążył sprawdzić - dla
            pozostałych `monitoring` jest nullem i nie ma czego pokazywać. */}
        {service.monitoring && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "monitoring"}
            className={`${styles.tab} ${tab === "monitoring" ? styles.tabActive : ""}`}
            onClick={() => setTab("monitoring")}
          >
            <Icon name="eye" />
            Monitoring
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={tab === "regulamin"}
          className={`${styles.tab} ${tab === "regulamin" ? styles.tabActive : ""}`}
          onClick={() => setTab("regulamin")}
        >
          <Icon name="file-text" />
          Regulamin
        </button>
      </div>

      {tab === "hosting" && service.hosting && (
        <section className={styles.tabPanel} role="tabpanel">
          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>Dostęp do panelu serwera</h4>

            {service.hosting.locked && (
              <FormStatus variant="warning">
                Konto jest zablokowane, bo usługa nie została przedłużona w terminie. Do panelu
                nadal się zalogujesz, ale strony, poczta i FTP nie działają. Przedłużenie usługi
                przywraca dostęp od razu.
              </FormStatus>
            )}

            <dl className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <dt>Adres panelu</dt>
                <dd>
                  <a href={service.hosting.panel_url} target="_blank" rel="noreferrer">
                    {service.hosting.panel_url}
                  </a>
                </dd>
              </div>
              <div className={styles.detailItem}>
                <dt>Login</dt>
                <dd>{service.hosting.login}</dd>
              </div>
              {service.hosting.created_at && (
                <div className={styles.detailItem}>
                  <dt>Konto założone</dt>
                  <dd>{formatDate(service.hosting.created_at)}</dd>
                </div>
              )}
            </dl>

            {/* Hasła nie znamy - powstaje przy zakładaniu konta, idzie do klienta raz mailem
                i nie jest u nas przechowywane. Dlatego zamiast je pokazywać, kierujemy do
                formularza odzyskiwania w panelu serwera. */}
            <p className={styles.note}>
              Hasło wysłaliśmy mailem przy zakładaniu konta i nie przechowujemy go u siebie.
              Jeśli go nie masz, ustaw nowe w panelu serwera.
            </p>

            <p className={styles.renewActions}>
              <a
                href={service.hosting.panel_url}
                className={styles.renewBtn}
                target="_blank"
                rel="noreferrer"
                title="Otwórz panel serwera"
              >
                <Icon name="arrow-square-out" className={styles.renewIcon} />
                Przejdź do panelu
              </a>
              <a
                href={service.hosting.password_reset_url}
                className={styles.rowButton}
                target="_blank"
                rel="noreferrer"
                title="Ustaw nowe hasło do panelu serwera"
              >
                <Icon name="lock" className={styles.renewIcon} />
                Zmień hasło
              </a>
            </p>
          </section>
        </section>
      )}

      {tab === "monitoring" && service.monitoring && (
        <section className={styles.tabPanel} role="tabpanel">
          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>Stan strony</h4>

            {service.monitoring.is_up ? (
              <FormStatus variant="success">
                Strona {service.monitoring.url} odpowiada poprawnie. Sprawdzamy ją co minutę.
              </FormStatus>
            ) : (
              <FormStatus variant="warning">
                Strona {service.monitoring.url} nie odpowiada od {formatDate(service.monitoring.since)}.
                Sprawdzamy ją dalej i napiszemy, gdy wróci do działania.
              </FormStatus>
            )}

            <dl className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <dt>Adres</dt>
                <dd>{service.monitoring.url}</dd>
              </div>
              <div className={styles.detailItem}>
                <dt>Ostatnie sprawdzenie</dt>
                <dd>{formatDate(service.monitoring.last_check) || "-"}</dd>
              </div>
              <div className={styles.detailItem}>
                <dt>Czas odpowiedzi</dt>
                <dd>
                  {service.monitoring.response_time !== null
                    ? `${service.monitoring.response_time.toFixed(3)} s`
                    : "-"}
                </dd>
              </div>
              <div className={styles.detailItem}>
                <dt>Dostępność</dt>
                <dd>
                  {service.monitoring.uptime_percent}% z ostatnich {service.monitoring.uptime_days} dni
                </dd>
              </div>
              {service.monitoring.ssl_days !== null && (
                <div className={styles.detailItem}>
                  <dt>Certyfikat SSL</dt>
                  <dd>
                    {service.monitoring.ssl_days >= 0
                      ? `ważny jeszcze ${service.monitoring.ssl_days} dni`
                      : `wygasł ${Math.abs(service.monitoring.ssl_days)} dni temu`}
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>Historia zdarzeń</h4>

            {monitoringLoading && <p className={styles.note}>Wczytuję historię...</p>}
            {monitoringError && <FormStatus variant="warning">{monitoringError}</FormStatus>}

            {!monitoringLoading && !monitoringError && monitoring && monitoring.events.length === 0 && (
              <p className={styles.note}>
                Brak zdarzeń - od początku monitorowania strona działa, a certyfikat jest ważny.
              </p>
            )}

            {monitoring && monitoring.events.length > 0 && (
              <dl className={styles.detailGrid}>
                {monitoring.events.map((event, i) => (
                  <div key={`${i}-${event.date}`} className={styles.detailItem}>
                    <dt>{formatDate(event.date)}</dt>
                    <dd>
                      {event.type_name}
                      {event.message && <span className={styles.detailNote}>{event.message}</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        </section>
      )}

      {tab === "regulamin" && (
        <section className={styles.tabPanel} role="tabpanel">
          <ServiceRegulaminTab
            regulamin={regulamin}
            loading={regulaminLoading}
            error={regulaminError}
          />
        </section>
      )}

      {tab === "info" && (
        <section className={styles.tabPanel} role="tabpanel">
          <div className={styles.serviceHeadline}>
            <span className={styles.name}>
              {service.type_name ?? `Typ ${service.type}`}
              {service.is_priority && (
                <span className={styles.priorityBadge}>
                  <Icon name="warning" />
                  Priorytetowa
                </span>
              )}
            </span>
            {service.package_name && <span className={styles.domain}>{service.package_name}</span>}
          </div>
  
          <dl className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <dt>Numer</dt>
              <dd>{service.nr}</dd>
            </div>
            <div className={styles.detailItem}>
              <dt>Status</dt>
              <dd>
                <Badge variant={statusVariant}>{service.status_name ?? `Status ${service.id_status}`}</Badge>
              </dd>
            </div>
            <div className={styles.detailItem}>
              <dt>Projekt</dt>
              <dd>{service.project_name ?? "-"}</dd>
            </div>
            <div className={styles.detailItem}>
              <dt>Uruchomiona</dt>
              <dd>{formatDate(service.add_date) || "-"}</dd>
            </div>
            <div className={styles.detailItem}>
              <dt>Wygasa</dt>
              <dd>
                {service.expiry_date ? formatDate(service.expiry_date) : "bezterminowo"}
                {daysAfter > 0 && (
                  <span className={styles.detailNoteWarn}>
                    wygasła {daysAfter} {dayWord(daysAfter)} temu
                  </span>
                )}
                {daysLeft !== null && !service.is_expired && (
                  <span className={styles.detailNote}>
                    pozostało {daysLeft} {dayWord(daysLeft)}
                  </span>
                )}
              </dd>
            </div>
            {service.resignation && (
              <div className={styles.detailItem}>
                <dt>Rezygnacja</dt>
                <dd>
                  {service.resignation_date
                    ? formatDate(service.resignation_date)
                    : "złożona"}
                </dd>
              </div>
            )}
            {/* Dodatkowe pola usługi - parametry zdefiniowane w pakiecie i uzupełnione
                przez operatora (np. "Liczba godzin obsługi w miesiącu" dla SLA).
                Backend przysyła tylko te oznaczone jako widoczne dla klienta. */}
            {service.additional_fields?.map((field, i) => (
              <div key={`${i}-${field.name}`} className={styles.detailItem}>
                <dt>{field.name}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
  
          {service.description && (
            <section className={styles.detailSection}>
              <h4 className={styles.detailSectionTitle}>Opis</h4>
              <div className={styles.richText}>{service.description}</div>
            </section>
          )}
  
          <section className={styles.detailSection}>
            <h4 className={styles.detailSectionTitle}>Przedłużenie</h4>
  
            {/* Automat przedłużenia jest ważniejszy od pozostałych stanów: klient nie ma
                tu nic do zrobienia, więc nie każemy mu czekać na przycisk ani wracać
                bliżej terminu. Gdy przedłużenie da się dodatkowo wykonać ręcznie
                (`renew_href`), przycisk zostaje - klient może przedłużyć wcześniej. */}
            {service.auto_renew ? (
              <>
                <FormStatus variant="success">
                  Ta usługa przedłuża się automatycznie - nie musisz nic robić.
                  Przed terminem wystawimy dokument na kolejny okres i wyślemy go mailem.
                </FormStatus>
                {service.renew_href && (
                  <p className={styles.renewActions}>
                    <a
                      href={service.renew_href}
                      className={styles.rowButton}
                      title={`Przedłuż usługę ${service.nr}`}
                    >
                      <Icon name="arrow-clockwise" className={styles.renewIcon} />
                      Przedłuż wcześniej
                    </a>
                  </p>
                )}
              </>
            ) : service.renew_href ? (
              <>
                <p className={styles.note}>
                  Usługę można teraz przedłużyć. Przycisk otwiera panel przedłużenia,
                  w którym wybierzesz okres i sposób płatności.
                </p>
                {service.is_expired && (
                  <FormStatus variant="warning">
                    {service.is_after_grace
                      ? "Okres na przedłużenie minął - usługa jest przeznaczona do usunięcia. Skontaktuj się z nami, zanim ją przedłużysz."
                      : `Usługa wygasła. Przedłuż ją do ${formatDate(service.grace_until)}, aby jej nie stracić.`}
                  </FormStatus>
                )}
                {/* Panel przedłużenia stoi w domenie backendu, a ID usługi jest w adresie
                    zaszyfrowane - klient nie musi się tam logować. Stąd zwykły link,
                    bez proxy przez route handler. */}
                <p className={styles.renewActions}>
                  <a
                    href={service.renew_href}
                    className={styles.renewBtn}
                    title={`Przedłuż usługę ${service.nr}`}
                  >
                    <Icon name="arrow-clockwise" className={styles.renewIcon} />
                    Przedłuż usługę
                  </a>
                </p>
              </>
            ) : !service.renew_allowed ? (
              <FormStatus variant="info">
                Tej usługi nie da się przedłużyć - jest rozliczana jednorazowo i wygasa
                po zakończeniu okresu. Jeśli chcesz ją kontynuować, skontaktuj się z nami,
                a dobierzemy odpowiedni pakiet.
              </FormStatus>
            ) : service.is_expired ? (
              <FormStatus variant="warning">
                Termin przedłużenia tej usługi już minął. Skontaktuj się z nami - sprawdzimy,
                czy da się ją jeszcze przywrócić.
              </FormStatus>
            ) : (
              <FormStatus variant="info">
                Przedłużenie tej usługi nie jest jeszcze dostępne. Przycisk pojawi się tutaj,
                gdy zbliży się termin jej wygaśnięcia - powiadomimy Cię też mailem.
              </FormStatus>
            )}
          </section>
        </section>
      )}
    </div>
  );
}

/**
 * Zakładka z regulaminem usługi.
 *
 * Pokazuje dokument OBOWIĄZUJĄCY dziś dla typu (ewentualnie pakietu) usługi razem ze
 * śladem zgody zapisanym przy usłudze. Akceptacji tutaj nie ma - klient wyraża ją
 * w panelu przedłużenia, a tu ma wgląd w to, na co się zgodził.
 *
 * Brak dokumentu NIE jest błędem ani pustą zakładką: dla części typów usług operator
 * regulaminu nie ustala i klient ma to przeczytać wprost, zamiast zgadywać, czy panel
 * się zepsuł.
 *
 * Gdy zaakceptowany dokument jest INNY niż obowiązujący (`accepted_outdated`), obok
 * treści zostaje link do wersji, na którą klient faktycznie się zgodził - ślad zgody
 * nie może zniknąć przez podmianę dokumentu.
 */
function ServiceRegulaminTab({
  regulamin,
  loading,
  error,
}: {
  regulamin: ServiceRegulamin | null;
  loading: boolean;
  error: string | null;
}) {
  if (error) return <FormStatus variant="error">{error}</FormStatus>;
  /* Brak danych bez błędu = zapytanie jeszcze nie wystartowało albo trwa. Jedna gałąź
     dla obu przypadków, inaczej pierwszy render po kliknięciu zakładki dawałby pustkę. */
  if (loading || !regulamin) return <FormStatus variant="info">Wczytywanie regulaminu...</FormStatus>;

  return (
    <>
      {regulamin.accepted ? (
        <FormStatus variant="success">
          {regulamin.accept_date
            ? `Regulamin zaakceptowany ${formatDate(regulamin.accept_date)}.`
            : "Regulamin tej usługi został zaakceptowany."}
        </FormStatus>
      ) : (
        <FormStatus variant="info">
          Akceptację regulaminu zbieramy przy zamawianiu i przedłużaniu usługi -
          przy tej usłudze nie mamy jej jeszcze zapisanej.
        </FormStatus>
      )}

      {regulamin.accepted_outdated && (
        <FormStatus variant="warning">
          Poniżej jest regulamin obowiązujący dla tej usługi dzisiaj. Zgoda została
          udzielona na wcześniejszy dokument
          {regulamin.accepted_href ? (
            <>
              {" - "}
              <a href={regulamin.accepted_href} target="_blank" rel="noopener noreferrer">
                otwórz zaakceptowaną wersję
              </a>
            </>
          ) : (
            "."
          )}
        </FormStatus>
      )}

      {regulamin.has_regulamin ? (
        <>
          {/* Treść pochodzi z modułu Usługi i jest sanityzowana po stronie serwera -
              renderujemy ją jak pozostałe treści redakcyjne w CMS. */}
          <div
            className={`${styles.richText} ${styles.regulaminText}`}
            dangerouslySetInnerHTML={{ __html: regulamin.text }}
          />
          {regulamin.href && (
            <p className={styles.renewActions}>
              {/* Podgląd stoi w domenie backendu pod zaszyfrowanym adresem (klient nie
                  musi się tam logować), więc zwykły link, bez proxy przez route handler. */}
              <a
                href={regulamin.href}
                className={styles.rowButton}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="arrow-square-out" />
                Otwórz w nowej karcie
              </a>
            </p>
          )}
        </>
      ) : (
        <FormStatus variant="info">
          Dla tej usługi nie ustalono odrębnego regulaminu. Warunki wynikają z umowy
          i zamówienia - w razie pytań skontaktuj się z nami.
        </FormStatus>
      )}
    </>
  );
}
